// ContactUs.jsx
import React, { useState } from 'react';
import { useSelector } from 'react-redux'; 
import { useNavigate, Link } from 'react-router-dom';
import useFetch from '../hooks/useFetch';

const ContactUs = () => {
  
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();

  const [formData, setFormData] = useState({
    name: '',
    loginId: '',
    email: '',
    contactNumber: '',
    inquiryType: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Comprehensive email validation regex
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // Indian mobile number validation regex (10 digits starting with 6-9)
  const phoneRegex = /^[6-9]\d{9}$/;

  const validateForm = () =>  {
    let isValid = true;
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact Number is required';
      isValid = false;
    } else if (!phoneRegex.test(formData.contactNumber)) {
      newErrors.contactNumber = 'Contact Number must be a valid 10-digit mobile number (starting with 6-9)';
      isValid = false;
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
      isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      isValid = false;
    } else if (formData.message.length > 500) {
      newErrors.message = 'Message must be 500 characters or less';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (validateForm()) {
      const config = {
        url: "/contact-us",
        method: "post",
        data: formData,
        headers: { Authorization: authState.token }
      };
  
      try {
        const response = await fetchData(config);

        if (response && response.success && response.message === 1) {
          setSubmitStatus("Thank you for your message. We will get back to you as soon as possible.");
        } else {
          setSubmitStatus("Sorry, we encountered an issue while processing your message. Please try again.");
        }
        setIsSubmitted(true);
        // Reset form data
        setFormData({
          name: "",
          loginId: "",
          email: "",
          contactNumber: "",
          inquiryType: "",
          message: ""
        });
        setErrors({});
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmitStatus("Sorry, there was an error processing your request. Please try again later.");
        setIsSubmitted(true);
      }
    }
  };

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col bg-gray-900">
        {/* Header */}
        <header className="bg-gray-800 text-white px-4 py-6 w-full">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center">
            Think5Whys Contact Us
          </h2>
        </header>

        {/* Confirmation Message */}
        <main className="flex-grow mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <div className="text-green-400 text-lg font-semibold mb-4">
              Message Sent!
            </div>
            <p className="text-white text-sm mb-6">{submitStatus}</p>
            <button 
              onClick={() => {
                setIsSubmitted(false);
                setSubmitStatus('');
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors"
            >
              Send Another Message
            </button>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-black border-t border-blue-800 text-white py-4 w-full mt-auto">
          <div className="mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="text-xs sm:text-sm lg:text-lg text-yellow-200 text-center md:text-left">
                © {new Date().getFullYear()} Think5Whys Global DeepTech Solutions.
              </div>
              <nav className="flex flex-wrap justify-center md:justify-end gap-2 sm:gap-4 text-xs sm:text-sm">
                <Link to="/about-us" className="text-white hover:text-yellow-200">About</Link>
                <Link to="/privacy-policy" className="text-white hover:text-yellow-200">Privacy Policy</Link>
                <Link to="/terms-of-use" className="text-white hover:text-yellow-200">Terms of Use</Link>
                <Link to="/contact-us" className="text-white hover:text-yellow-200">Contact</Link>
              </nav>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-black border-b border-blue-400 text-white px-4 py-6 w-full">
        <h2 className="text-xl lg:text-2xl text-yellow-200 font-bold text-center">
          Think5Whys Contact Us
        </h2>
      </header>

      {/* Main Content */}
      <main className="flex-grow w-full px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="w-full bg-black rounded-lg shadow-lg p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
              Name <span className="text-red-400">*</span>
            </label>
            <input 
              id="name"
              type="text" 
              name="name" 
              value={formData.name} 
              onChange={handleChange}
              placeholder="Full Name"
              aria-describedby={errors.name ? "name-error" : undefined}
              aria-invalid={!!errors.name}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
            {errors.name && (
              <span id="name-error" className="block text-red-400 text-xs mt-1" role="alert">
                {errors.name}
              </span>
            )}
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
              Email <span className="text-red-400">*</span>
            </label>
            <input 
              id="email"
              type="email" 
              name="email" 
              value={formData.email} 
              onChange={handleChange}
              placeholder="your@email.com"
              aria-describedby={errors.email ? "email-error" : undefined}
              aria-invalid={!!errors.email}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
            {errors.email && (
              <span id="email-error" className="block text-red-400 text-xs mt-1" role="alert">
                {errors.email}
              </span>
            )}
          </div>
          
          <div>
            <label htmlFor="contactNumber" className="block text-sm font-medium text-white mb-2">
              Contact Number <span className="text-red-400">*</span>
            </label>
            <input 
              id="contactNumber"
              type="tel" 
              name="contactNumber" 
              value={formData.contactNumber} 
              onChange={handleChange}
              placeholder="e.g., 9324342890"
              pattern="[6-9][0-9]{9}"
              aria-describedby={errors.contactNumber ? "contact-error" : undefined}
              aria-invalid={!!errors.contactNumber}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
            {errors.contactNumber && (
              <span id="contact-error" className="block text-red-400 text-xs mt-1" role="alert">
                {errors.contactNumber}
              </span>
            )}
          </div>
          
          <div>
            <label htmlFor="inquiryType" className="block text-sm font-medium text-white mb-2">
              Inquiry Type <span className="text-red-400">*</span>
            </label>
            <select 
              id="inquiryType"
              name="inquiryType" 
              value={formData.inquiryType} 
              onChange={handleChange}
              aria-describedby={errors.inquiryType ? "inquiry-error" : undefined}
              aria-invalid={!!errors.inquiryType}
              className="w-full h-10 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              <option value="">Select...</option>
              <option value="Feedback">Feedback</option>
              <option value="Query">Query</option>
              <option value="Complaint">Complaint</option>
            </select>
            {errors.inquiryType && (
              <span id="inquiry-error" className="block text-red-400 text-xs mt-1" role="alert">
                {errors.inquiryType}
              </span>
            )}
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
              Message <span className="text-red-400">*</span>
            </label>
            <textarea 
              id="message"
              name="message" 
              value={formData.message} 
              onChange={handleChange} 
              maxLength={500}
              style={{width: "100%", height: "300px"}}
              placeholder="Enter your message here..."
              aria-describedby={`${errors.message ? "message-error " : ""}message-counter`}
              aria-invalid={!!errors.message}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors"
            />
            {errors.message && (
              <span id="message-error" className="block text-red-400 text-xs mt-1" role="alert">
                {errors.message}
              </span>
            )}
            <div id="message-counter" className="text-right mt-1">
              <span className={`text-xs ${formData.message.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>
                {formData.message.length}/500
              </span>
            </div>
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-bold py-3 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors disabled:cursor-not-allowed"
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </main>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-800 text-white py-4 w-full mt-auto">
        <div className="mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-xs sm:text-sm lg:text-lg text-yellow-200 text-center md:text-left">
              © {new Date().getFullYear()} Think5Whys Global DeepTech Solutions.
            </div>
            <nav className="flex flex-wrap justify-center md:justify-end gap-2 sm:gap-4 text-xs sm:text-sm">
              <Link to="/about-us" className="text-white hover:text-yellow-200">About</Link>
              <Link to="/privacy-policy" className="text-white hover:text-yellow-200">Privacy Policy</Link>
              <Link to="/terms-of-use" className="text-white hover:text-yellow-200">Terms of Use</Link>
              <Link to="/contact-us" className="text-white hover:text-yellow-200">Contact</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;